// Knowledge entry editor — modal with title (1..120) + textarea (1..20000)
// + manual `.txt`/`.md` import. formModal caps at 2000 chars, so this is its
// own dialog with a destroyable overlay, explicit destination confirmation
// (checkbox required even for typed entries) and UTF-8 strict decoder.
import { h } from './dom.js';

type ScopeLike = { kind: 'person' | 'group'; id: string; label: string };

const TITLE_MAX = 120;
const TEXT_MAX = 20000;
const FILE_MAX = 20480;
const ALLOWED_EXT = ['.txt', '.md'];

export type KnowledgeEditorResult = {
  title: string;
  text: string;
};

type Opts = {
  title: string;
  scope: ScopeLike;
  initialTitle?: string;
  initialText?: string;
  confirmLabel?: string;
  signal: AbortSignal;
  onSave(values: {title:string;text:string}): Promise<void>;
};

async function readFileSafe(file: File): Promise<string> {
  if (file.size > FILE_MAX) throw new Error(`El archivo supera el límite de ${FILE_MAX} bytes.`);
  const ab = await file.arrayBuffer();
  let bytes: Uint8Array;
  if (ab instanceof Uint8Array) bytes = ab;
  else bytes = new Uint8Array(ab);
  if (bytes.length === 0) throw new Error('El archivo está vacío.');
  if (bytes.includes(0)) throw new Error('El archivo contiene bytes NUL; parece binario y no se permite.');
  let decoded: string;
  try {
    decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new Error('El archivo no es UTF-8 válido.');
  }
  return decoded;
}

export function openKnowledgeEditor(opts: Opts): Promise<KnowledgeEditorResult | null> {
  return new Promise(resolve => {
    if (opts.signal.aborted) { resolve(null); return; }
    const previous = document.activeElement as HTMLElement | null;
    let closed = false;
    let fileGeneration = 0;
    const overlay = h('div', { class: 'modal-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-label': opts.title });
    const card = h('form', { class: 'modal-card form-stack knowledge-editor' });
    card.append(h('h2', { class: 'modal-title' }, [opts.title]));

    card.append(h('p', { class: 'knowledge-editor-dest muted-text' }, [
      'Destino: ', h('strong', {}, [opts.scope.label]),
      opts.scope.kind === 'group'
        ? ' (grupo · entradas compartidas con sus miembros).'
        : ' (persona · entradas privadas de este espacio).',
    ]));

    const titleInput = h('input', {
      class: 'input',
      type: 'text',
      required: true,
      maxlength: TITLE_MAX,
      'aria-label': 'Título',
    }) as HTMLInputElement;
    titleInput.value = opts.initialTitle ?? '';
    const titleCount = h('span', { class: 'char-count muted-text' }, [`${titleInput.value.length}/${TITLE_MAX}`]);
    titleInput.addEventListener('input', () => { titleCount.textContent = `${titleInput.value.length}/${TITLE_MAX}`; });
    const titleField = h('label', { class: 'field' }, [
      h('span', {}, ['Título (1–' + TITLE_MAX + ' caracteres)']),
      titleInput,
      titleCount,
    ]);
    card.append(titleField);

    const textarea = h('textarea', {
      class: 'textarea knowledge-editor-textarea',
      required: true,
      rows: 10,
      maxlength: TEXT_MAX,
      'aria-label': 'Contenido',
    }) as HTMLTextAreaElement;
    textarea.value = opts.initialText ?? '';
    const textCount = h('span', { class: 'char-count muted-text' }, [`${textarea.value.length}/${TEXT_MAX}`]);
    textarea.addEventListener('input', () => { textCount.textContent = `${textarea.value.length}/${TEXT_MAX}`; });
    const textField = h('label', { class: 'field' }, [
      h('span', {}, ['Contenido (1–' + TEXT_MAX + ' caracteres)']),
      textarea,
      textCount,
    ]);
    card.append(textField);

    const fileInput = h('input', {
      class: 'input knowledge-editor-file',
      type: 'file',
      accept: '.txt,.md,text/plain,text/markdown',
      'aria-label': 'Importar archivo de texto',
    }) as HTMLInputElement;
    const fileStatus = h('p', { class: 'muted-text' });
    const preview = h('pre', { class: 'knowledge-editor-preview muted-text', hidden: true });
    fileInput.addEventListener('change', async () => {
      const fileToken = ++fileGeneration;
      confirmBox.checked = false;
      fileStatus.textContent = '';
      preview.hidden = true;
      const f = fileInput.files?.[0];
      if (!f) return;
      const lower = f.name.toLowerCase();
      if (!ALLOWED_EXT.some(ext => lower.endsWith(ext))) {
        fileStatus.textContent = 'Solo se admiten archivos .txt o .md.';
        fileInput.value = '';
        return;
      }
      try {
        const text = await readFileSafe(f);
        if (closed || opts.signal.aborted || fileToken !== fileGeneration) return;
        if (!text.trim()) throw new Error('El archivo no contiene texto.');
        if (text.length > TEXT_MAX) throw new Error('El texto supera los 20.000 caracteres. Reduce el archivo antes de importarlo.');
        textarea.value = text;
        textCount.textContent = `${textarea.value.length}/${TEXT_MAX}`;
        preview.textContent = text.slice(0, 600) + (text.length > 600 ? '\n…' : '');
        preview.hidden = false;
        fileStatus.textContent = `Archivo cargado (${f.name}, ${text.length} caracteres). Revisa y edita antes de guardar.`;
      } catch (err) {
        if (closed || opts.signal.aborted || fileToken !== fileGeneration) return;
        fileStatus.textContent = (err as Error).message;
        fileInput.value = '';
      }
    });
    const fileField = h('label', { class: 'field' }, [
      h('span', {}, ['Importar archivo (.txt o .md, máx. 20 KiB)']),
      fileInput,
      fileStatus,
      preview,
    ]);
    card.append(fileField);

    const confirmBox = h('input', {
      class: 'knowledge-editor-confirm',
      type: 'checkbox',
      required: true,
    }) as HTMLInputElement;
    const confirmLabelText = `Confirmo que quiero guardar esta entrada en “${opts.scope.label}”.`;
    const confirmField = h('label', { class: 'field knowledge-editor-confirm-field' }, [
      confirmBox,
      h('span', {}, [confirmLabelText]),
    ]);
    card.append(confirmField);

    const error = h('p', { class: 'auth-error', role: 'alert' });
    const cancel = h('button', { class: 'btn ghost', type: 'button' }, ['Cancelar']);
    const submit = h('button', { class: 'btn primary', type: 'submit' }, [opts.confirmLabel ?? 'Guardar']);
    let pending = false;


    const finish = (result: KnowledgeEditorResult | null) => {
      if (closed) return;
      closed = true; fileGeneration++;
      opts.signal.removeEventListener('abort', onAbort);
      overlay.remove();
      titleInput.value = ''; textarea.value = ''; preview.textContent = '';
      if (previous?.isConnected) previous.focus();
      resolve(result);
    };
    const onAbort = () => finish(null);
    const close = () => { if (!pending) finish(null); };
    opts.signal.addEventListener('abort', onAbort, {once:true});
    cancel.addEventListener('click', close);

    card.append(error, h('div', { class: 'modal-actions' }, [cancel, submit]));
    overlay.append(card);
    document.body.append(overlay);

    const trap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      const controls = Array.from(overlay.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),textarea:not([disabled])'));
      const first = controls[0]; const last = controls.at(-1);
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    };
    overlay.addEventListener('keydown', trap);

    card.addEventListener('submit', async e => {
      e.preventDefault();
      if (pending || closed || opts.signal.aborted) return;
      const title = titleInput.value.trim();
      const text = textarea.value.trim();
      if (title.length < 1 || title.length > TITLE_MAX) {
        error.textContent = `Título entre 1 y ${TITLE_MAX} caracteres.`;
        return;
      }
      if (text.length < 1 || text.length > TEXT_MAX) {
        error.textContent = `Contenido entre 1 y ${TEXT_MAX} caracteres.`;
        return;
      }
      if (!confirmBox.checked) {
        error.textContent = 'Marca la casilla de confirmación del destino antes de guardar.';
        return;
      }
      pending = true; error.textContent = '';
      submit.disabled = cancel.disabled = true;
      try {
        await opts.onSave({title,text});
        if (!closed && !opts.signal.aborted) finish({title,text});
      } catch (err) {
        if (!closed && !opts.signal.aborted) error.textContent = (err as Error).message || 'No se pudo guardar. Puedes reintentar.';
      } finally { pending = false; if (!closed) submit.disabled = cancel.disabled = false; }
    });

    titleInput.focus();
  });
}

export const KNOWLEDGE_EDITOR_LIMITS = { TITLE_MAX, TEXT_MAX, FILE_MAX };
